import type React from 'react';
import type { IntlProvider, MessageFormatElement } from 'react-intl';

type IntlProviderProps = React.ComponentProps<typeof IntlProvider>;

export const defaultRichTextElements: IntlProviderProps['defaultRichTextElements'] =
  {
    b: (chunks) => <b>{chunks}</b>,
  };

export type AvailableLocale =
  | 'ar'
  | 'be'
  | 'bg'
  | 'bs'
  | 'cs'
  | 'da'
  | 'de'
  | 'el'
  | 'en-GB'
  | 'en-US'
  | 'es'
  | 'fi'
  | 'fr'
  | 'fr-CA'
  | 'he'
  | 'hi-IN'
  | 'hr'
  | 'ht'
  | 'hu'
  | 'is'
  | 'it'
  | 'ja'
  | 'ka'
  | 'lb'
  | 'mk'
  | 'ml'
  | 'nl'
  | 'no'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'ru'
  | 'sk'
  | 'sl'
  | 'sv'
  | 'tl'
  | 'uk';

/**
 * Maps all possible browser locales to our supported locales.
 * If a browser locale isn't found here, we fall back to en-US.
 */
export const LOCALE_MAPPING: Record<string, AvailableLocale> = {
  // Arabic
  ar: 'ar',
  'ar-001': 'ar',
  'ar-AE': 'ar',
  'ar-BH': 'ar',
  'ar-DJ': 'ar',
  'ar-DZ': 'ar',
  'ar-EG': 'ar',
  'ar-EH': 'ar',
  'ar-ER': 'ar',
  'ar-IL': 'ar',
  'ar-IQ': 'ar',
  'ar-JO': 'ar',
  'ar-KM': 'ar',
  'ar-KW': 'ar',
  'ar-LB': 'ar',
  'ar-LY': 'ar',
  'ar-MA': 'ar',
  'ar-MR': 'ar',
  'ar-OM': 'ar',
  'ar-PS': 'ar',
  'ar-QA': 'ar',
  'ar-SA': 'ar',
  'ar-SD': 'ar',
  'ar-SO': 'ar',
  'ar-SS': 'ar',
  'ar-SY': 'ar',
  'ar-TD': 'ar',
  'ar-TN': 'ar',
  'ar-YE': 'ar',

  // Belarusian
  be: 'be',
  'be-BY': 'be',

  // Bulgarian
  bg: 'bg',
  'bg-BG': 'bg',

  // Bosnian
  bs: 'bs',
  'bs-Cyrl': 'bs',
  'bs-Cyrl-BA': 'bs',
  'bs-Latn': 'bs',
  'bs-Latn-BA': 'bs',

  // Czech
  cs: 'cs',
  'cs-CZ': 'cs',

  // Danish
  da: 'da',
  'da-DK': 'da',
  'da-GL': 'da',

  // German
  de: 'de',
  'de-AT': 'de',
  'de-BE': 'de',
  'de-CH': 'de',
  'de-DE': 'de',
  'de-IT': 'de',
  'de-LI': 'de',
  'de-LU': 'de',

  // Greek
  el: 'el',
  'el-CY': 'el',
  'el-GR': 'el',

  // English
  en: 'en-US',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  // All other English variants default to en-US
  'en-001': 'en-US',
  'en-150': 'en-US',
  'en-AG': 'en-US',
  'en-AI': 'en-US',
  'en-AS': 'en-US',
  'en-AT': 'en-US',
  'en-AU': 'en-US',
  'en-BB': 'en-US',

  // Spanish
  es: 'es',
  'es-419': 'es',
  'es-AR': 'es',
  'es-BO': 'es',
  'es-BR': 'es',
  'es-BZ': 'es',
  'es-CL': 'es',
  'es-CO': 'es',
  'es-CR': 'es',
  'es-CU': 'es',
  'es-DO': 'es',
  'es-EA': 'es',
  'es-EC': 'es',
  'es-ES': 'es',
  'es-GQ': 'es',
  'es-GT': 'es',
  'es-HN': 'es',
  'es-IC': 'es',
  'es-MX': 'es',
  'es-NI': 'es',
  'es-PA': 'es',
  'es-PE': 'es',
  'es-PH': 'es',
  'es-PR': 'es',
  'es-PY': 'es',
  'es-SV': 'es',
  'es-US': 'es',
  'es-UY': 'es',
  'es-VE': 'es',

  // Finnish
  fi: 'fi',
  'fi-FI': 'fi',

  // French
  fr: 'fr',
  'fr-BE': 'fr',
  'fr-BF': 'fr',
  'fr-BI': 'fr',
  'fr-BJ': 'fr',
  'fr-BL': 'fr',
  'fr-CA': 'fr-CA',
  'fr-CD': 'fr',
  'fr-CF': 'fr',
  'fr-CG': 'fr',
  'fr-CH': 'fr',
  'fr-CI': 'fr',
  'fr-CM': 'fr',
  'fr-DJ': 'fr',
  'fr-DZ': 'fr',
  'fr-FR': 'fr',
  'fr-GA': 'fr',
  'fr-GF': 'fr',
  'fr-GN': 'fr',
  'fr-GP': 'fr',
  'fr-GQ': 'fr',
  'fr-HT': 'fr',
  'fr-KM': 'fr',
  'fr-LU': 'fr',
  'fr-MA': 'fr',
  'fr-MC': 'fr',
  'fr-MF': 'fr',
  'fr-MG': 'fr',
  'fr-ML': 'fr',
  'fr-MQ': 'fr',
  'fr-MR': 'fr',
  'fr-MU': 'fr',
  'fr-NC': 'fr',
  'fr-NE': 'fr',
  'fr-PF': 'fr',
  'fr-PM': 'fr',
  'fr-RE': 'fr',
  'fr-RW': 'fr',
  'fr-SC': 'fr',
  'fr-SN': 'fr',
  'fr-SY': 'fr',
  'fr-TD': 'fr',
  'fr-TG': 'fr',
  'fr-TN': 'fr',
  'fr-VU': 'fr',
  'fr-WF': 'fr',
  'fr-YT': 'fr',

  // Hebrew
  he: 'he',
  'he-IL': 'he',

  // Hindi
  hi: 'hi-IN',
  'hi-IN': 'hi-IN',

  // Croatian
  hr: 'hr',
  'hr-BA': 'hr',
  'hr-HR': 'hr',

  // Haitian Creole
  ht: 'ht',

  // Hungarian
  hu: 'hu',
  'hu-HU': 'hu',

  // Icelandic
  is: 'is',
  'is-IS': 'is',

  // Italian
  it: 'it',
  'it-CH': 'it',
  'it-IT': 'it',
  'it-SM': 'it',
  'it-VA': 'it',

  // Japanese
  ja: 'ja',
  'ja-JP': 'ja',

  // Georgian
  ka: 'ka',
  'ka-GE': 'ka',

  // Luxembourgish
  lb: 'lb',
  'lb-LU': 'lb',

  // Macedonian
  mk: 'mk',
  'mk-MK': 'mk',

  // Malayalam
  ml: 'ml',
  'ml-IN': 'ml',

  // Dutch
  nl: 'nl',
  'nl-AW': 'nl',
  'nl-BE': 'nl',
  'nl-BQ': 'nl',
  'nl-CW': 'nl',
  'nl-NL': 'nl',
  'nl-SR': 'nl',
  'nl-SX': 'nl',

  // Norwegian
  no: 'no',
  'nb-NO': 'no',
  'nb-SJ': 'no',
  'nn-NO': 'no',

  // Polish
  pl: 'pl',
  'pl-PL': 'pl',

  // Portuguese
  pt: 'pt',
  'pt-AO': 'pt',
  'pt-BR': 'pt',
  'pt-CH': 'pt',
  'pt-CV': 'pt',
  'pt-GQ': 'pt',
  'pt-GW': 'pt',
  'pt-LU': 'pt',
  'pt-MO': 'pt',
  'pt-MZ': 'pt',
  'pt-PT': 'pt',
  'pt-ST': 'pt',
  'pt-TL': 'pt',

  // Romanian
  ro: 'ro',
  'ro-MD': 'ro',
  'ro-RO': 'ro',

  // Russian
  ru: 'ru',
  'ru-BY': 'ru',
  'ru-KG': 'ru',
  'ru-KZ': 'ru',
  'ru-MD': 'ru',
  'ru-RU': 'ru',
  'ru-UA': 'ru',

  // Slovak
  sk: 'sk',
  'sk-SK': 'sk',

  // Slovenian
  sl: 'sl',
  'sl-SI': 'sl',

  // Swedish
  sv: 'sv',
  'sv-AX': 'sv',
  'sv-FI': 'sv',
  'sv-SE': 'sv',

  // Tagalog
  tl: 'tl',
  'fil-PH': 'tl',

  // Ukrainian
  uk: 'uk',
  'uk-UA': 'uk',
};

export const AVAILABLE_LOCALES: AvailableLocale[] = [
  'ar',
  'be',
  'bg',
  'bs',
  'cs',
  'da',
  'de',
  'el',
  'en-GB',
  'en-US',
  'es',
  'fi',
  'fr',
  'fr-CA',
  'he',
  'hi-IN',
  'hr',
  'ht',
  'hu',
  'is',
  'it',
  'ja',
  'ka',
  'lb',
  'mk',
  'ml',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'tl',
  'uk',
];

/** One locale's compiled messages, as `react-intl` consumes them. */
export type LocaleMessages =
  | Record<string, string>
  | Record<string, MessageFormatElement[]>;

export type Translations = {
  [locale in AvailableLocale]: LocaleMessages;
};

export interface LanguageProviderProps {
  locale: AvailableLocale;
  /**
   * Messages resolved on the server for the locale the tree starts in, so the
   * first render is already translated and nothing waits on a chunk. A later
   * `locale` change loads its own set.
   */
  initialMessages: { locale: AvailableLocale; messages: LocaleMessages };
  timeZone?: string;
  children: React.ReactNode;
}
